// ─────────────────────────────────────────────
// Oike — the reach of the commercial profiles in a client base.
// One rule for the browser (useAppData, to show) and the server
// (airtable-proxy and the Gmail and enrichment functions, to allow):
//   admin   everything in its base
//   bdr     the accounts assigned to them, and what hangs from those accounts
//   cp      the accounts of their client partner record(s), and what hangs from them
//   viewer  (and any other role) reads like before, never writes
// The base itself is checked apart (the token's base, airtable-proxy).
// Oike's central base («equipo»): only admin and bdr enter, and a bdr there is
// stricter (teamScope below): identified by the email of the login (one Users
// record with that exact Email, never by name), reaching only the accounts whose
// Accounts › BDR links exactly that record, and only TEAM_TABLES.
// Tests: npm run test:commercial-scope.
// ─────────────────────────────────────────────

/** Data key → how its records hang from an account. `self`: the account itself. */
export const SCOPED_TABLES = {
  accounts: { self: true },
  stakeholders: { links: ['Account'] },
  opportunities: { links: ['Account'] },
  outreach: { links: ['Account'], stakeholderLinks: ['Stakeholder'] },
  actionPlan: { links: ['Cuenta'] },
  diagnosticIntake: { links: ['Account'] },
  proposals: { links: ['Account'] },
};

import { classifyRecipient, hasQuote } from './personQuotes.js';

/**
 * Personal quotes (Quote Recipient = person), quotes whose recipient is not certain («Por revisar»,
 * src/personQuotes.js › classifyRecipient) and their emitted documents are only for the
 * administration of the base (and for the recipient, in her own space). Managing the company
 * or its contacts gives no access to them, nor does the company linked as a reference: not to
 * bdr, cp or read-only profiles, in lists, reads, writes, publishing or documents. Any other
 * commercial delegation will have to be defined explicitly.
 */
export const ADMIN_ONLY_TABLES = ['diagnosticIntake', 'landings'];
const selectName = (v) => (v && typeof v === 'object' && !Array.isArray(v) ? v.name : v) || '';
export const isPersonalQuoteFields = (f = {}) => selectName(f['Quote Recipient']) === 'person';
export function isPersonalDocumentFields(f = {}) {
  try { return JSON.parse(f['Proposal Snapshot JSON'] || 'null')?.recipient === 'person'; } catch { return false; }
}
const isProposalDocument = (f = {}) => !!f['Proposal Snapshot JSON'] || selectName(f['Type']) === 'Consulting Proposal';
/**
 * Whether a record of this table is administration-only: a quote that is not certainly a company's
 * (personal or «Por revisar»; a request without a quote keeps its usual reach), and a proposal
 * document to a person or not filed under a company.
 */
export function adminOnlyRecord(tableKey, record) {
  const f = record?.fields || {};
  // «Solo administración» (Admin Only, an explicit decision of the administration) only adds protection: a personal
  // or unclassified quote stays protected with the box empty, and an empty box never grants anything by itself
  if (ADMIN_ONLY_TABLES.includes(tableKey) && f[ADMIN_ONLY_FIELD] === true) return true;
  if (tableKey === 'diagnosticIntake') return hasQuote(f) && classifyRecipient(f).kind !== 'company';
  if (tableKey === 'landings') return isProposalDocument(f) && (isPersonalDocumentFields(f) || !linkedIdsOf({ fields: f }, 'Account').length);
  return false;
}
/** Diagnostic Intake › Admin Only and Landings › Admin Only (checkbox): written only by quote-recipient.mts. */
export const ADMIN_ONLY_FIELD = 'Admin Only';
/** Whether a write would make a record administration-only (a profile that is not admin never does it). */
export function writeMakesAdminOnly(tableKey, fields = {}) {
  if (tableKey === 'diagnosticIntake') return Object.prototype.hasOwnProperty.call(fields, 'Quote Recipient') && selectName(fields['Quote Recipient']) !== 'company';
  if (tableKey === 'landings') return isPersonalDocumentFields(fields);
  return false;
}

/** The only tables a bdr of Oike's central base reaches (everything else is refused by the server). */
export const TEAM_TABLES = ['accounts', 'stakeholders', 'outreach', 'opportunities'];
/** Account fields that say who works it: in the central base only the administration writes them (team.mts › assign). */
export const TEAM_ASSIGNMENT_FIELDS = ['BDR', 'BDR Owner', 'Assignment History'];
/** Accounts › Assignment History (long text): one JSON line per assignment, newest first (team.mts writes it). */
export const ASSIGNMENT_HISTORY_FIELD = 'Assignment History';

const normEmail = (v) => String(v ?? '').trim().toLowerCase().replace(/^mailto:/, '');
/**
 * The Users record of the login, by the authenticated email: exactly one record with that Email.
 * Never by name. { status: 'ok', record } | { status: 'missing' } | { status: 'ambiguous', count }.
 */
export function teamUserOf(users = [], email) {
  const e = normEmail(email);
  if (!e) return { status: 'missing' };
  const same = users.filter(u => normEmail(u.fields?.['Email']) === e);
  if (same.length === 1) return { status: 'ok', record: same[0] };
  return same.length ? { status: 'ambiguous', count: same.length } : { status: 'missing' };
}

/**
 * Who works one account, as the central base decides it: only Accounts › BDR, one linked Users record.
 *   assigned — exactly one (userId);  multiple — more than one (nobody of the team reaches it: Ale resolves);
 *   legacy   — no BDR link but an older way says someone (the BDR Owner name; or a Users › Accounts link — in the
 *              central base Users › Accounts is the inverse of Accounts › BDR, so it cannot differ: kept only as a
 *              defence for bases where it is a separate field): Ale resolves;
 *   none     — nobody.
 */
export function teamAssignmentOf(account, users = []) {
  const links = linkedIdsOf(account, OWNER_FIELDS.bdrLink);
  if (links.length === 1) return { state: 'assigned', userId: links[0] };
  if (links.length > 1) return { state: 'multiple', userIds: links };
  const ownerText = textOf(account?.fields?.[OWNER_FIELDS.bdrOwner]).trim();
  const viaUsers = users.filter(u => linkedIdsOf(u, 'Accounts').includes(account?.id)).map(u => u.id);
  if (ownerText || viaUsers.length) return { state: 'legacy', ownerText, userIds: viaUsers };
  return { state: 'none' };
}

/** The scope of a bdr of the central base: { kind: 'bdr', accountIds, stakeholderIds, identity }. */
export function teamScopeOf(email, { accounts = [], users = [], stakeholders = [] } = {}) {
  const identity = teamUserOf(users, email);
  const myId = identity.status === 'ok' ? identity.record.id : null;
  const accountIds = new Set(myId ? accounts.filter(a => { const t = teamAssignmentOf(a); return t.state === 'assigned' && t.userId === myId; }).map(a => a.id) : []);
  const stakeholderIds = new Set(stakeholders.filter(s => linkedIdsOf(s, 'Account').some(id => accountIds.has(id))).map(s => s.id));
  return { kind: 'bdr', accountIds, stakeholderIds, identity: { status: identity.status, userId: myId } };
}

/** Tables that define who sees what: only an admin writes them. */
export const SCOPE_DEFINING_TABLES = ['users', 'clientPartners'];

/** The account fields that make an account someone's (read by the rule, stamped on a new account). */
export const OWNER_FIELDS = { bdrLink: 'BDR', bdrOwner: 'BDR Owner', cpLink: 'CP' };

export function scopeKindOf(role) {
  const r = String(role || '').toLowerCase();
  if (r === 'admin') return 'full';
  if (r === 'bdr' || r === 'cp') return r;
  return 'readonly';
}

const lower = (v) => String(v ?? '').toLowerCase().trim();
export const linkedIdsOf = (rec, field) => {
  const v = rec?.fields?.[field];
  if (Array.isArray(v)) return v.map(x => (typeof x === 'string' ? x : x?.id)).filter(Boolean);
  return typeof v === 'string' && v.startsWith('rec') ? [v] : [];
};
const textOf = (v) => (Array.isArray(v) ? v.map(x => (typeof x === 'string' ? x : x?.name || '')).join(', ') : typeof v === 'object' && v ? v.name || '' : String(v ?? ''));

/**
 * The ids of the accounts one user reaches.
 * user: { role, name, email, clientRecordId?, assignedAccountIds? }
 * tables: { accounts, users, clientPartners } (records as Airtable returns them)
 * Returns null for a role that reaches every account (admin, viewer).
 */
export function visibleAccountIdsOf(user, { accounts = [], users = [], clientPartners = [] } = {}) {
  const kind = scopeKindOf(user?.role);
  if (kind === 'full' || kind === 'readonly') return null;
  const ids = new Set();
  if (kind === 'bdr') {
    const name = lower(user?.name);
    const email = lower(user?.email);
    const me = users.find(r => (user?.clientRecordId && r.id === user.clientRecordId) || (name && lower(r.fields?.['Name']) === name));
    const myId = me?.id || user?.clientRecordId || '';
    for (const id of user?.assignedAccountIds || []) ids.add(id);
    for (const id of me ? linkedIdsOf(me, 'Accounts') : []) ids.add(id);
    for (const a of accounts) {
      if (myId && linkedIdsOf(a, OWNER_FIELDS.bdrLink).includes(myId)) ids.add(a.id);
      const owner = lower(textOf(a.fields?.[OWNER_FIELDS.bdrOwner]));
      if (owner && ((name && owner === name) || (email && owner === email))) ids.add(a.id);
    }
  }
  if (kind === 'cp') {
    const myCp = myClientPartnerIds(user, clientPartners);
    for (const a of accounts) if (linkedIdsOf(a, OWNER_FIELDS.cpLink).some(id => myCp.includes(id))) ids.add(a.id);
  }
  // Only accounts that exist (an assignment to a deleted account reaches nothing)
  const existing = new Set(accounts.map(a => a.id));
  return new Set([...ids].filter(id => existing.has(id)));
}

export function myClientPartnerIds(user, clientPartners = []) {
  const email = lower(user?.email);
  return email ? clientPartners.filter(cp => lower(cp.fields?.['Email']) === email).map(cp => cp.id) : [];
}

/** The scope of one user: { kind, accountIds: Set|null, stakeholderIds: Set|null }. stakeholders is needed only for outreach. */
export function scopeOf(user, tables = {}) {
  const kind = scopeKindOf(user?.role);
  const accountIds = visibleAccountIdsOf(user, tables);
  let stakeholderIds = null;
  if (accountIds) {
    stakeholderIds = new Set((tables.stakeholders || [])
      .filter(s => linkedIdsOf(s, 'Account').some(id => accountIds.has(id)))
      .map(s => s.id));
  }
  return { kind, accountIds, stakeholderIds };
}

/** Whether one record of a scoped table is within reach. Tables that are not scoped are always within reach. */
export function recordInScope(tableKey, record, scope) {
  const rule = SCOPED_TABLES[tableKey];
  if (!rule || !scope?.accountIds) return true;
  if (rule.self) return scope.accountIds.has(record?.id);
  if ((rule.links || []).some(f => linkedIdsOf(record, f).some(id => scope.accountIds.has(id)))) return true;
  if ((rule.stakeholderLinks || []).some(f => linkedIdsOf(record, f).some(id => scope.stakeholderIds?.has(id)))) return true;
  return false;
}

/**
 * Whether the links a write sets stay within reach: every account (and, for
 * outreach, every contact) it links has to be reachable. A write that links
 * nothing is allowed (the record stays outside everyone's reach but an admin's).
 */
export function linksInScope(tableKey, fields = {}, scope) {
  const rule = SCOPED_TABLES[tableKey];
  if (!rule || rule.self || !scope?.accountIds) return true;
  const rec = { fields };
  for (const f of rule.links || []) if (!linkedIdsOf(rec, f).every(id => scope.accountIds.has(id))) return false;
  for (const f of rule.stakeholderLinks || []) if (!linkedIdsOf(rec, f).every(id => scope.stakeholderIds?.has(id))) return false;
  return true;
}

/** The fields that make a new account belong to whoever creates it (bdr: BDR Owner = their name; cp: their client partner records). */
export function ownerStampFor(user, clientPartners = []) {
  const kind = scopeKindOf(user?.role);
  if (kind === 'bdr') return user?.name ? { [OWNER_FIELDS.bdrOwner]: user.name } : null;
  if (kind === 'cp') {
    const mine = myClientPartnerIds(user, clientPartners);
    return mine.length ? { [OWNER_FIELDS.cpLink]: mine } : null;
  }
  return {};
}

/** The browser's filter of the loaded data (same rule as the server, which already filtered it). */
export function filterLoadedData(results, user) {
  if (scopeKindOf(user?.role) !== 'full') {
    if (Array.isArray(results.diagnosticIntakes)) results.diagnosticIntakes = results.diagnosticIntakes.filter(r => !adminOnlyRecord('diagnosticIntake', r));
    if (Array.isArray(results.landings)) results.landings = results.landings.filter(r => !adminOnlyRecord('landings', r));
  }
  // A bdr of the central base: the server already filtered with the strict team rule (by its Users record id)
  if (user?.teamMode) return results;
  const scope = scopeOf(user, results);
  if (!scope.accountIds) return results;
  const keyOf = { diagnosticIntakes: 'diagnosticIntake' };
  for (const dataKey of ['accounts', 'stakeholders', 'opportunities', 'outreach', 'actionPlan', 'diagnosticIntakes', 'proposals']) {
    const tableKey = keyOf[dataKey] || dataKey;
    if (Array.isArray(results[dataKey])) results[dataKey] = results[dataKey].filter(r => recordInScope(tableKey, r, scope));
  }
  return results;
}
